/**
 * Catalog registry — lazy-loaded catalog JSON files.
 *
 * Each entry maps a catalog ID to a dynamic import thunk.
 * The actual JSON is only loaded when the user selects a catalog.
 */

import type { ICatalog } from '@/workflows/vascular/common/catalog';

type CatalogLoader = () => Promise<ICatalog>;

/**
 * Registry mapping catalog IDs to their lazy-loaded JSON data.
 *
 * Catalog JSON files are added to `data/` as they are converted from
 * the C++ desktop application's SQLite databases. Entries without a
 * corresponding JSON file should be commented out until the data is available.
 */
export const CATALOG_REGISTRY: Record<string, CatalogLoader> = {
    'artivion-etegra': () => import('./data/artivion-etegra.json').then((m) => m.default as ICatalog),
    'cook-zenith-alpha': () => import('./data/cook-zenith-alpha.json').then((m) => m.default as ICatalog),
    'gore-excluder-ous': () => import('./data/gore-excluder-ous.json').then((m) => m.default as ICatalog),
    'medtronic-endurant-iis-ous': () =>
        import('./data/medtronic-endurant-iis-ous.json').then((m) => m.default as ICatalog),
    'terumo-treo-ous': () => import('./data/terumo-treo-ous.json').then((m) => m.default as ICatalog),
};

/**
 * Load a catalog by ID.
 * @returns The catalog data, or null if not found in the registry.
 */
export async function loadCatalog(catalogId: string): Promise<ICatalog | null> {
    const loader = CATALOG_REGISTRY[catalogId];
    if (!loader) return null;
    return loader();
}
